import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dashboardService from 'ApiService/Admin/dashboardService';
import Card from 'components/card';
import { AuthContext } from 'contexts/AuthContext';
import toast from 'react-hot-toast';

const statusConfig = {
  'New': { bg: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', label: 'New' },
  'Will Attend': { bg: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Will Attend' },
  'Cannot Attend': { bg: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Cannot Attend' },
  'In Interview': { bg: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200', label: 'In Interview' },
  'Interview Scheduled': { bg: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200', label: 'Interview Scheduled' },
  'Offer Sent': { bg: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200', label: 'Offer Sent' },
  'Accepted': { bg: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Accepted' },
  'Declined': { bg: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Declined' },
  'Not Selected': { bg: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Not Selected' },
};

const getStatus = (a) => {
  const { studentJobResponse, overallStatus, studentRevert, viewedAt, scheduledInterviews } = a;
  const hasInterviews = scheduledInterviews?.length > 0;

  if (overallStatus === 'Not Selected' || overallStatus === 'Rejected') return 'Not Selected';
  if (studentJobResponse === 'Cannot Attend') return 'Cannot Attend';
  if (overallStatus === 'Selected' && studentRevert === 'Pending') return 'Offer Sent';
  if (overallStatus === 'Selected' && studentRevert === 'Accepted') return 'Accepted';
  if (overallStatus === 'Selected' && (studentRevert === 'Declined' || studentRevert === 'Auto Declined')) return 'Declined';
  if (studentJobResponse === 'Will Attend' && hasInterviews) return 'Interview Scheduled';
  if (studentJobResponse === 'Will Attend') return 'In Interview';
  if (!viewedAt && studentJobResponse === 'Pending') return 'New';
  return overallStatus || 'Assigned';
};

const RecentAssignments = () => {
  const { state } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  const role = state?.user?.role;
  const hrRole = state?.user?.hrRole;
  const selectedBranch = state?.selectedBranch;
  const searchRef = useRef(null);

  const fetchData = async (searchTerm = search) => {
    setLoading(true);
    try {
      const response = await dashboardService.getRecentAssignments(selectedBranch, 10, searchTerm);
      if (response.data.success) {
        setAssignments(response.data.data);
        setTotal(response.data.total);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData('');
    setSearch('');
  }, [selectedBranch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const isHR =
    role === 'placement' || role === 'headhr' ||
    hrRole === 'headhr' || hrRole === 'hr' || hrRole === 'jrhr';

  if (!isHR && role !== 'admin' && role !== 'superadmin') return null;

  const getBasePath = () => {
    if (role === 'headhr' || hrRole === 'headhr') return '/headhr';
    if (role === 'placement' || hrRole === 'hr' || hrRole === 'jrhr') return '/placement';
    if (role === 'superadmin') return '/superadmin';
    return '/admin';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <Card extra="pb-5 p-4">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">
            Recent Job Assignments
          </h3>
          <span className="rounded-full bg-brand-500 px-3 py-0.5 text-xs font-medium text-white">
            {total} Total
          </span>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchData} disabled={loading}
            className="flex items-center gap-1 rounded-xl border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:border-white/10 dark:text-gray-300 dark:hover:bg-navy-700"
          >
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          <button onClick={() => navigate(`${getBasePath()}/hr/assignment-overview`)}
            className="rounded-xl bg-brand-500 px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-brand-600"
          >
            View All
          </button>
        </div>
      </div>

      <div className="relative mb-4">
        <input
          ref={searchRef}
          type="text"
          placeholder="Search by name, email or number..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-xl border border-gray-200 bg-lightPrimary py-2.5 pl-10 pr-3 text-sm outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
        />
        <svg className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {search && (
          <button onClick={() => { setSearch(''); fetchData(''); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      ) : assignments.length === 0 ? (
        <div className="flex h-32 items-center justify-center">
          <p className="text-sm text-gray-500">No assignments found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-white/10 dark:text-gray-400">
                <th className="pb-3 pr-4">Student</th>
                <th className="pb-3 pr-4">Company</th>
                <th className="pb-3 pr-4">Role</th>
                <th className="pb-3 pr-4">Assigned</th>
                <th className="pb-3 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => {
                const status = getStatus(a);
                const cfg = statusConfig[status] || { bg: 'bg-gray-100 text-gray-800', label: status };
                return (
                  <tr key={a._id}
                    onClick={() => navigate(`${getBasePath()}/hr/assignment-overview`)}
                    className="cursor-pointer border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-white/5 dark:hover:bg-navy-700"
                  >
                    <td className="py-3 pr-4">
                      <p className="text-sm font-medium text-navy-700 dark:text-white">
                        {a.studentId?.name || '-'}
                      </p>
                      <p className="text-xs text-gray-500">{a.studentId?.email || ''}</p>
                    </td>
                    <td className="py-3 pr-4 text-sm text-gray-600 dark:text-gray-300">
                      {a.companyId?.name || '-'}
                    </td>
                    <td className="py-3 pr-4 text-sm text-gray-600 dark:text-gray-300">
                      {a.roleId?.roleName || a.jobTitle || '-'}
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(a.createdAt)}
                    </td>
                    <td className="py-3 text-right">
                      <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${cfg.bg}`}>
                        {cfg.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default RecentAssignments;
